import React from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export function PageTitle({ children, sub, actions, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6" data-testid={testid}>
      <div>
        <h1 className="font-display text-[28px] leading-[34px] font-bold text-nb-ink">{children}</h1>
        {sub && <p className="text-[14px] text-nb-ink2 mt-1">{sub}</p>}
      </div>
      {actions}
    </div>
  );
}

export function SectionTitle({ children, className = "" }) {
  return <h2 className={`text-[18px] leading-[26px] font-semibold text-nb-ink ${className}`}>{children}</h2>;
}

export function Label({ children, className = "" }) {
  return <span className={`nb-label ${className}`}>{children}</span>;
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div
      className={`bg-nb-surface rounded-2xl shadow-[0_1px_2px_rgba(23,36,63,0.06),0_4px_16px_rgba(23,36,63,0.05)] ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export function MetricTip({ label, def }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-help text-nb-ink2">
          {label}
          <Info size={13} className="opacity-60" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px] text-[13px] leading-relaxed">{def}</TooltipContent>
    </Tooltip>
  );
}

export function Takeaway({ children, testid }) {
  if (!children) return null;
  return (
    <p className="text-[13px] text-nb-ink2 mb-3" data-testid={testid}>
      {children}
    </p>
  );
}

// Change badge: arrow + sign + 1-decimal % + comparison period.
// higherIsBetter=false flips colour (a rise is red, a fall is green); arrow always shows true direction.
export function Delta({ change, higherIsBetter = true, period = "vs last period", testid }) {
  if (change === null || change === undefined) return null;
  const zero = Math.abs(change) < 0.0005;
  const up = change > 0;
  const arrow = zero ? "●" : up ? "▲" : "▼";
  const sign = zero ? "" : up ? "+" : "−";
  const mag = (Math.abs(change) * 100).toFixed(1);
  let tone = "zero";
  if (!zero) tone = (higherIsBetter ? up : !up) ? "pos" : "neg";
  const styles = {
    pos: { color: "var(--pos-text)", background: "var(--pos-bg)" },
    neg: { color: "var(--neg-text)", background: "var(--neg-bg)" },
    zero: { color: "var(--text-muted)", background: "transparent" },
  }[tone];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[13px] font-medium tnum whitespace-nowrap"
      style={styles}
      data-testid={testid}
    >
      {arrow} {sign}{mag}%{period ? <span className="font-normal opacity-75"> {period}</span> : null}
    </span>
  );
}

// Back-compat alias used across pages.
export function ChangeArrow({ change, higherIsBetter = true, period = "vs last period", testid }) {
  return <Delta change={change} higherIsBetter={higherIsBetter} period={period} testid={testid} />;
}

export function UtilBar({ value, overCap, testid }) {
  const width = Math.min(1, value || 0) * 100;
  return (
    <div className="w-full" data-testid={testid}>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-sunken)" }}>
        <div
          className="h-full rounded-full transition-[width] duration-200"
          style={{ width: `${width}%`, backgroundColor: "var(--brand-kawaii)" }}
        />
      </div>
      {overCap && <span className="text-[12px] text-nb-ink2 mt-1 inline-block">More seats than active users</span>}
    </div>
  );
}

const STATUS_STYLES = {
  "High usage": "var(--brand-kawaii)",
  "Moderate usage": "var(--brand-floral)",
  "Low usage": "var(--border-control)",
  "Too early to tell": "var(--text-muted)",
};

export function StatusChip({ status, testid }) {
  const dot = STATUS_STYLES[status] || "var(--text-muted)";
  return (
    <span
      className="inline-flex items-center gap-2 text-[13px] text-nb-ink rounded-md px-2.5 py-1"
      style={{ background: "var(--bg-sunken)" }}
      data-testid={testid}
    >
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dot }} />
      {status}
    </span>
  );
}

export function PartialChip() {
  return (
    <span
      className="inline-flex items-center text-[12px] text-nb-ink rounded-md px-2.5 py-0.5"
      style={{ backgroundColor: "var(--brand-floral)" }}
    >
      Partial coverage
    </span>
  );
}

export function LoadingBlocks({ rows = 3, testid = "loading" }) {
  return (
    <div className="space-y-3" data-testid={testid}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-16 rounded-2xl animate-pulse" style={{ background: "var(--bg-sunken)" }} />
      ))}
    </div>
  );
}

export function EmptyState({ title, next, action, testid = "empty-state" }) {
  return (
    <Card className="p-10 text-center" data-testid={testid}>
      <p className="text-[16px] text-nb-ink font-medium">{title}</p>
      {next && <p className="text-[14px] text-nb-ink2 mt-2">{next}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </Card>
  );
}

export function ErrorState({ error, onRetry, testid = "error-state" }) {
  return (
    <Card className="p-8 text-center" data-testid={testid}>
      <p className="text-[15px] text-nb-ink">{error?.message || "That did not load."}</p>
      <div className="mt-3 flex items-center justify-center gap-3">
        {onRetry && (
          <button
            onClick={onRetry}
            data-testid="error-retry"
            className="text-[14px] px-4 py-2 rounded-[10px] bg-nb-navy text-nb-cream font-semibold hover:opacity-90 transition-opacity"
          >
            Try again
          </button>
        )}
      </div>
      {error?.code && <p className="text-[12px] text-nb-ink2 mt-3">Code: {error.code}</p>}
    </Card>
  );
}

export function Forbidden({ who = "an Admin", testid = "forbidden-state" }) {
  return (
    <Card className="p-10 text-center" data-testid={testid}>
      <p className="text-[16px] text-nb-ink font-medium">This view is limited</p>
      <p className="text-[14px] text-nb-ink2 mt-2">
        {who} can see this. Ask your AI Lead to grant access or change the setting.
      </p>
    </Card>
  );
}

export function GroupTooSmall({ testid = "group-too-small" }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <span className="flex-1 h-3 rounded-full" style={{ backgroundImage: "repeating-linear-gradient(45deg,#E3DED0,#E3DED0 4px,#F7F3E6 4px,#F7F3E6 8px)" }} />
      <span className="text-[13px] text-nb-ink2 italic whitespace-nowrap">Fewer than 5 people, hidden to protect individuals</span>
    </div>
  );
}
